import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Check, CheckCheck } from 'lucide-react'
import { notificationsApi } from '../../api/endpoints'
import Button from '../../components/ui/Button'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import { fmtDateTime } from '../../utils/date'

const PAGE_SIZE = 15

const typeIcons: Record<string, string> = {
  task_due: '\ud83d\udccb',
  task_overdue: '\u26a0\ufe0f',
  task_assigned: '\ud83d\udc64',
  task_completed: '\u2705',
  call_reminder: '\ud83d\udcde',
  email_bounce: '\ud83d\udce7',
  wa_incoming: '\ud83d\udcac',
  lead_assigned: '\ud83c\udfaf',
  missed_call: '\ud83d\udcf5',
  vip_missed_call: '\ud83d\udea8',
  all_agents_offline: '\ud83d\udeab',
  quality_score_drop: '\ud83d\udcc9',
}

export default function NotificationsPage() {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<'unread' | 'all'>('unread')
  const [offset, setOffset] = useState(0)

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', filter, offset],
    queryFn: () => notificationsApi.list({
      unread_only: String(filter === 'unread'),
      limit: String(PAGE_SIZE),
      offset: String(offset),
    }),
  })

  const markReadMutation = useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'count'] })
    },
  })

  const markAllMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'count'] })
    },
  })

  const notifications = data?.notifications || []
  const total = data?.total || 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          {total > 0 && (
            <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">{total}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => { setFilter('unread'); setOffset(0) }}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                filter === 'unread' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Unread
            </button>
            <button
              onClick={() => { setFilter('all'); setOffset(0) }}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                filter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All
            </button>
          </div>
          {filter === 'unread' && notifications.length > 0 && (
            <Button variant="secondary" size="sm" onClick={() => markAllMutation.mutate()} loading={markAllMutation.isPending}>
              <CheckCheck size={14} /> Mark all read
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>
      ) : notifications.length === 0 ? (
        <EmptyState
          icon={Bell}
          title={filter === 'unread' ? 'No unread notifications' : 'No notifications'}
          description={filter === 'unread' ? "You're all caught up!" : 'Nothing here yet.'}
        />
      ) : (
        <>
          <div className="space-y-1.5 max-w-2xl">
            {notifications.map((n: any) => (
              <div
                key={n.id}
                className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                  n.is_read ? 'bg-white border-gray-100' : 'bg-indigo-50/50 border-indigo-100'
                }`}
              >
                <span className="text-lg flex-shrink-0 mt-0.5">{typeIcons[n.notification_type] || '\ud83d\udd14'}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${n.is_read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>{n.title}</p>
                  {n.body && <p className="text-xs text-gray-500 mt-0.5">{n.body}</p>}
                  <p className="text-xs text-gray-400 mt-1">{fmtDateTime(n.created_at)}</p>
                </div>
                {!n.is_read && (
                  <button
                    onClick={() => markReadMutation.mutate(n.id)}
                    className="p-2.5 text-gray-400 hover:text-indigo-600 rounded flex-shrink-0"
                    aria-label="Mark as read"
                    title="Mark as read"
                  >
                    <Check size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="max-w-2xl">
            <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
          </div>
        </>
      )}
    </div>
  )
}
